import React, { useState, useEffect } from 'react';
import {
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Icon,
  Accordion,
  Button,
  Popup
} from 'semantic-ui-react';
import { servicesAPI } from '../../_services';
import { history } from '../../_helpers/';
import { ChargePoint } from '../../_components/map/Features/ChargePoint';
import {
  copyToClipboard,
  dbUTCDatetimeToLocal,
  polyFillStructuredClone
} from '../../utils';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
import './customerDetail_styles.css';

const moment = extendMoment(Moment);

export const ChargingSessions = () => {
  const [hubLocationInfo, setHubLocationInfo] = useState(
    window.hubLocationInfo
  );
  const [loadingHubInfo, setLoadingHubInfo] = useState(true);

  const [chargeStationStates, setChargeStationStates] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [faulted, setFaulted] = useState([]);
  const [openSessions, setOpenSessions] = useState([]);

  const [sessionsHide, setSessionsHide] = useState(false);
  const [faultedHide, setFaultedHide] = useState(false);
  const [openSessionsHide, setOpenSessionsHide] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [previousConsumptions, setPreviousConsumptions] = useState({});

  const NO_UID = '00000000-0000-0000-0000-000000000000';

  async function getChargeStationStates() {
    try {
      let data = await servicesAPI.AdminServices(
        'getStatesByEntityType',
        'CHARGING_STATION'
      );

      let chargingSessions = [];
      let chargeStationStates = [];

      const keys = Object.keys(data);
      for (const key of keys) {
        try {
          const state = JSON.parse(data[key]);

          if (state.customer_name) {
            // Fix UTF-8 decoding in customer names
            const bits = state.customer_name.split('u0');
            let name = '';
            bits.forEach((bit, idx) => {
              if (idx === 0) {
                name = name + bit;
              } else {
                const code = Number('0x' + bit.substring(0, 3));
                const remainder = bit.substring(3);
                const u = String.fromCharCode(parseInt(code));
                name = name + u + remainder;
              }
            });
            state.customer_name = name;
          }

          chargeStationStates.push(state);

          // Check for corruption. Occasionally an API call failure can cause issues
          if (!state.hydra_aon) continue;
          if (!state.phalanx) continue;
          if (!state.phalanx.reference) continue;
          if (state.phalanx.tag_status === 'INACTIVE') continue;

          const hydraInfo = hubLocationInfo.find(
            h => h.tag_id === state.hydra_aon.tag_id
          );
          const area = hydraInfo ? hydraInfo.local_authority : '';

          const location = `${
            state.hydra_aon.description
          } CS${state.phalanx.reference.slice(-2)}`;

          const muted = state.phalanx.status === 'Stopped';
          const faulted = state.phalanx.status === 'Faulted';
          const capped = state.phalanx.status === 'Capped';
          const maintenance = state.phalanx.tag_status === 'MAINTENANCE';

          const charging =
            ['Occupied', 'Faulted'].includes(state.phalanx.status) &&
            state.phalanx.connected_lance_tag_id !== NO_UID &&
            state.phalanx.connected_lance_tag_id &&
            state.lance &&
            state.session &&
            state.phalanx.transaction_id !== NO_UID;

          const occupied =
            state.phalanx.status === 'Occupied' ||
            (state.phalanx.status === 'Faulted' && state.lance);
          const overrideCharge =
            state.lance && state.lance.hasOwnProperty('override_charge_rate')
              ? state.lance.override_charge_rate
              : -1;
          const lanceDetected = state.state === 'HallsDetectedLance';

          // Data corruption from incorrectly started session?
          if (charging && state.session && !state.session.timestamp) {
            console.log(
              'Corrupted state on phalanx ' + state.phalanx.reference
            );
            continue;
          }

          try {
            if ((lanceDetected || charging || occupied || faulted) && !capped) {
              const chargingSession = {
                entity_id: state.entity_id,
                timestamp: state.timestamp,
                state: state.state,
                hydra_aon: state.hydra_aon,
                phalanx: state.phalanx,
                lance: state.lance,
                session: state.session,
                reading: state.reading,
                customer_id: state.customer_id,
                customer_name: state.customer_name,
                customer_notes: state.customer_notes,
                delayed_charging: ['Cheapest'].includes(
                  state.session?.analytics?.pref_charging_type
                )
                  ? '(delayed charge)'
                  : '',
                area: area,
                location: location,
                lance_detected: lanceDetected,
                charging: charging,
                override_charge: overrideCharge,
                occupied: occupied,
                muted: muted,
                maintenance: maintenance,
                faulted: faulted,
                capped: capped
              };

              chargingSessions.push(chargingSession);
            }
          } catch (error) {}

          // Check for outdated timestamps
          const dtLastHeartbeat = moment.utc(
            state.phalanx.last_heartbeat_time,
            'YYYY-MM-DDTHH:mm:ss,Z'
          );
          const dtNow = moment.utc().local();
          const elapsedMilliseconds = moment.duration(
            dtNow.diff(dtLastHeartbeat)
          )._milliseconds;

          // Flag comms lost
          state.phalanx.no_heartbeat = elapsedMilliseconds >= 900 * 1000;
        } catch (error) {}
      }

      setChargeStationStates(chargeStationStates);

      const activeSessions = chargingSessions
        .filter(cs => cs.session && !cs.faulted && cs.lance)
        .sort((a, b) =>
          b.session && a.session
            ? moment(b.session.timestamp, 'YYYY-MM-DDTHH:mm:ss,Z') -
              moment(a.session.timestamp, 'YYYY-MM-DDTHH:mm:ss,Z')
            : 0
        );

      const faulted = chargingSessions.filter(
        cs => !cs.muted && !cs.maintenance && (!cs.session || cs.faulted)
      );

      // Faulted charge stations
      setFaulted(faulted);
      setFaultedHide(faulted.length === 0);

      // Open sessions
      data = await servicesAPI.AdminServices(
        'getStatesByEntityType',
        'ACTIVE_CHARGING_SESSIONS'
      );

      try {
        const json = JSON.parse(data.ALL);

        let openSessions = json.sessions
          .filter(os => !activeSessions.find(as => as.session.id === os.id))
          .map(os => {
            const chargeStationState = chargeStationStates.find(
              cs => cs.phalanx?.tag_id === os.phalanx_tag_id
            );
            const location = chargeStationState
              ? `${
                  chargeStationState.hydra_aon.description
                } CS${chargeStationState.phalanx.reference.slice(-2)}`
              : 'Unknown location';
            return {
              ...os,
              location: location
            };
          });

        setOpenSessions(openSessions);
        setOpenSessionsHide(openSessions.length === 0);
      } catch (error) {}

      // If total consumption increased, infer C2 state (this can happen when
      // the Hydra state message for C2 failed to send or reach the CSMS)
      activeSessions.forEach(state => {
        const phalanxRef = state?.phalanx?.reference;
        const consumption = state?.session?.total_consumption;

        if (phalanxRef && Number.isInteger(consumption)) {
          if (
            state.state !== 'EvC2State' &&
            consumption - previousConsumptions[phalanxRef] > 0 &&
            previousConsumptions[phalanxRef]
          ) {
            state.state = 'EvC2State (inferred)';
          }
          previousConsumptions[phalanxRef] = consumption;
        }
      });
      setPreviousConsumptions(polyFillStructuredClone(previousConsumptions));

      setActiveSessions(activeSessions);
    } catch (error) {
      // console.log(error);
    }
  }

  const updateOpenSessions = () => {
    servicesAPI.MeteringServices('checkAlerts').then(
      response => {
        // Open sessions
        servicesAPI
          .AdminServices('getStatesByEntityType', 'ACTIVE_CHARGING_SESSIONS')
          .then(
            data => {
              try {
                const json = JSON.parse(data[0].value);

                let openSessions = json.sessions
                  .filter(
                    os => !activeSessions.find(as => as.session.id === os.id)
                  )
                  .map(os => {
                    const chargeStationState = chargeStationStates.find(
                      cs => cs.phalanx?.tag_id === os.phalanx_tag_id
                    );
                    const location = chargeStationState
                      ? `${
                          chargeStationState.hydra_aon.description
                        } CS${chargeStationState.phalanx.reference.slice(-2)}`
                      : 'Unknown location';
                    return {
                      ...os,
                      location: location
                    };
                  });

                setOpenSessions(openSessions);
                setOpenSessionsHide(openSessions.length === 0);
              } catch (error) {}
            },
            error => {}
          );
      },
      error => {}
    );
  };

  const displayState = s => {
    if (s.phalanx.current_state.startsWith('EvC2State')) {
      return (
        <p style={{ color: 'blue' }}>
          {s.phalanx.current_state}
          <br />
          {s.delayed_charging}
        </p>
      );
    }

    if (s.phalanx.axon_stopped) {
      return (
        <p style={{ color: 'red' }}>
          {s.phalanx.current_state}
          <br />
          (AXON stopped CS to prevent lance theft)
        </p>
      );
    }

    return (
      <p style={{ color: 'red' }}>
        {s.phalanx.current_state}
        <br />
        {s.delayed_charging}
      </p>
    );
  };

  useEffect(() => {
    if (!hubLocationInfo) return;

    getChargeStationStates();
    setLastUpdated(
      moment()
        .local()
        .format('HH:mm:ss')
    );

    // Refresh every 10 seconds
    const interval = setInterval(() => {
      getChargeStationStates();
      setLastUpdated(
        moment()
          .local()
          .format('HH:mm:ss')
      );
    }, 10000);
    return () => {
      clearInterval(interval);
    };
  }, [hubLocationInfo]);

  useEffect(() => {
    if (!hubLocationInfo && loadingHubInfo) {
      servicesAPI
        .InventoryServices('getPhalanxLocationInfo')
        .then(hubLocationInfo => {
          let newHubLocationInfo = [];
          for (const hydraRef of Object.keys(hubLocationInfo)) {
            newHubLocationInfo.push({
              ...hubLocationInfo[hydraRef],
              reference: hydraRef
            });
          }
          window.hubLocationInfo = newHubLocationInfo;

          setLoadingHubInfo(false);
          setHubLocationInfo(newHubLocationInfo);
        });
    }
  }, [hubLocationInfo, loadingHubInfo]);

  return (
    <>
      <Segment>
        <Accordion>
          <Accordion.Title
            active={!sessionsHide}
            index={0}
            onClick={() => {
              setSessionsHide(!sessionsHide);
            }}
          >
            <Grid stretched>
              <Grid.Row>
                <Grid.Column>
                  <Header as="h2">
                    <Icon name="dropdown" />
                    Charging Sessions ({activeSessions.length})
                  </Header>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Accordion.Title>
          <Accordion.Content active={!sessionsHide}>
            <Divider hidden />
            <Grid>
              <Table basic="very" selectable stackable celled fixed>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell
                      style={{ textAlign: 'center', width: '60px' }}
                    />
                    <Table.HeaderCell style={{ width: '100px' }}>
                      Started
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '60px' }}>
                      Local Authority
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '130px' }}>
                      Location
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '110px' }}>
                      Customer
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '80px' }}>
                      State
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '80px' }}>
                      Total Consumption (Wh)
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '70px' }}>
                      Charge Rate (kW)
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '80px' }}>
                      Arbitrated Limit (A)
                    </Table.HeaderCell>
                    {/*<Table.HeaderCell style={{ width: '70px' }}>
                      APC Limit (%)
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '70px' }}>
                      APC Boost
                    </Table.HeaderCell>*/}
                    <Table.HeaderCell style={{ width: '130px' }}>
                      Last Heartbeat
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {activeSessions.map((s, s_idx) => (
                    <Table.Row key={s_idx}>
                      <Table.Cell
                        style={{ textAlign: 'center' }}
                        onClick={() => {
                          copyToClipboard(s.phalanx.tag_id);

                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        <ChargePoint
                          w={48}
                          h={48}
                          colour={
                            s.charging
                              ? 'blue'
                              : s.muted
                              ? 'grey'
                              : s.faulted
                              ? 'red'
                              : 'green'
                          }
                          cpId={''}
                          l1={s.charging}
                          l2={s.charging}
                          l3={s.charging}
                          car={s.charging}
                          lance={s.occupied || s.lance_detected}
                        />
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          history.push(
                            `/admin/session/${s.session ? s.session.id : ''}`
                          );
                        }}
                      >
                        {s.faulted
                          ? 'Faulted'
                          : s.session
                          ? dbUTCDatetimeToLocal(s.session.timestamp)
                          : 'Not actively charging'}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          copyToClipboard(s.phalanx.reference);

                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.area}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          copyToClipboard(s.phalanx.reference);

                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.hydra_aon.description} CS
                        {s.phalanx.reference.slice(-2)}
                      </Table.Cell>
                      <Table.Cell>
                        <>
                          <div
                            onClick={() => {
                              history.push(`/admin/customer/${s.customer_id}`);
                            }}
                          >
                            {s.customer_name}
                          </div>
                          {s.customer_notes && (
                            <Popup
                              content={s.customer_notes}
                              trigger={
                                <Icon
                                  className="comment-button"
                                  color="grey"
                                  name="comment outline alternate"
                                  style={{ color: 'teal', fontSize: '14px' }}
                                />
                              }
                            />
                          )}
                        </>
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {displayState(s)}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          history.push(
                            `/admin/session/${s.session ? s.session.id : ''}`
                          );
                        }}
                      >
                        {s.session ? s.session.total_consumption : ''}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          history.push(
                            `/admin/session/${s.session ? s.session.id : ''}`
                          );
                        }}
                      >
                        {'charge_rate' in s.session
                          ? s.session.charge_rate.toFixed(0)
                          : ''}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.charging ? (
                          s.override_charge === -1 ? (
                            s.phalanx.arbitrated_limit
                          ) : (
                            <p style={{ color: 'blue' }}>
                              {s.override_charge} (manually overridden)
                            </p>
                          )
                        ) : (
                          ''
                        )}
                      </Table.Cell>
                      {/*<Table.Cell
                        onClick={() => {
                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.hydra_aon.project === 'SMARTSTEP'
                          ? s.phalanx.apc_limit
                          : 'n/a'}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.hydra_aon.project === 'SMARTSTEP' ? (
                          s.phalanx.boost === 1 ? (
                            <p style={{ color: 'blue' }}>{'ACTIVE'}</p>
                          ) : (
                            '-'
                          )
                        ) : (
                          'n/a'
                        )}
                      </Table.Cell>*/}
                      <Table.Cell
                        onClick={() => {
                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.phalanx?.no_heartbeat ? (
                          <p style={{ color: 'red' }}>
                            {dbUTCDatetimeToLocal(
                              s.phalanx.last_heartbeat_time
                            )}
                          </p>
                        ) : (
                          dbUTCDatetimeToLocal(s.phalanx.last_heartbeat_time)
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              <p>Last updated: {lastUpdated}</p>
            </Grid>
          </Accordion.Content>
        </Accordion>
      </Segment>
      <Segment>
        <Accordion>
          <Accordion.Title
            active={!faultedHide}
            index={0}
            onClick={() => {
              setFaultedHide(!faultedHide);
            }}
          >
            <Grid stretched>
              <Grid.Row>
                <Grid.Column>
                  <Header as="h2">
                    <Icon name="dropdown" />
                    Faulted Charge Points ({faulted.length})
                  </Header>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Accordion.Title>
          <Accordion.Content active={!faultedHide}>
            <Divider hidden />
            <Grid>
              <Table basic="very" selectable stackable celled fixed>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell
                      style={{ textAlign: 'center', width: '60px' }}
                    />
                    <Table.HeaderCell style={{ width: '100px' }}>
                      Location
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '80px' }}>
                      State
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '130px' }}>
                      Last Heartbeat
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {faulted.map((s, s_idx) => (
                    <Table.Row key={s_idx}>
                      <Table.Cell
                        style={{ textAlign: 'center' }}
                        onClick={() => {
                          copyToClipboard(s.phalanx.tag_id);

                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        <ChargePoint
                          w={48}
                          h={48}
                          colour={
                            s.charging
                              ? 'blue'
                              : s.muted
                              ? 'grey'
                              : s.faulted
                              ? 'red'
                              : 'green'
                          }
                          cpId={''}
                          l1={s.charging}
                          l2={s.charging}
                          l3={s.charging}
                          car={s.charging}
                          lance={s.occupied || s.lance_detected}
                        />
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          copyToClipboard(s.phalanx.reference);

                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.hydra_aon.description} CS
                        {s.phalanx.reference.slice(-2)}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          copyToClipboard(s.phalanx.reference);

                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.phalanx.current_state}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          const bits = s.phalanx.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {s.phalanx.last_heartbeat_time !== ''
                          ? dbUTCDatetimeToLocal(s.phalanx.last_heartbeat_time)
                          : ''}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Grid>
          </Accordion.Content>
        </Accordion>
      </Segment>
      <Segment>
        <Accordion>
          <Accordion.Title
            active={!openSessionsHide}
            index={0}
            onClick={() => {
              setOpenSessionsHide(!openSessionsHide);
            }}
          >
            <Grid stretched>
              <Grid.Row>
                <Grid.Column>
                  <Popup
                    content="Sessions that did not end properly. May include newly started sessions - click Refresh to clear these."
                    trigger={
                      <Header as="h2">
                        <Icon name="dropdown" />
                        Open Sessions ({openSessions.length})
                      </Header>
                    }
                  />{' '}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Accordion.Title>
          <Accordion.Content active={!openSessionsHide}>
            <Divider hidden />
            <Grid>
              <Table basic="very" selectable stackable celled fixed>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ width: '100px' }}>
                      Location
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '130px' }}>
                      Started
                    </Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '80px' }}>
                      Total Consumption (Wh)
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {openSessions.map((s, s_idx) => (
                    <Table.Row key={s_idx}>
                      <Table.Cell
                        onClick={() => {
                          history.push(`/admin/session/${s.id}`);
                        }}
                      >
                        {s.location}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          history.push(`/admin/session/${s.id}`);
                        }}
                      >
                        {dbUTCDatetimeToLocal(s.timestamp)}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          history.push(`/admin/session/${s.id}`);
                        }}
                      >
                        {s.total_consumption}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Grid>
            <br />
            <Button
              color="olive"
              onClick={() => {
                updateOpenSessions();
              }}
            >
              Refresh list
            </Button>
          </Accordion.Content>
        </Accordion>
      </Segment>
    </>
  );
};
